import React, { useState } from "react";
import { mesocycleTemplates } from "../data/templates";
import { Exercise, Mesocycle, Session } from "../types";

interface CreateMesocycleProps {
  onCancel: () => void;
  onCreate: (mesocycle: Omit<Mesocycle, "id">) => void;
}

export const CreateMesocycle: React.FC<CreateMesocycleProps> = ({
  onCancel,
  onCreate,
}) => {
  const [mode, setMode] = useState<"template" | "manual" | null>(null);
  const [name, setName] = useState("");
  const [weeks, setWeeks] = useState(6);
  const [sessions, setSessions] = useState<Session[]>([]);

  const handleTemplateSelect = (templateIndex: number) => {
    const template = mesocycleTemplates[templateIndex];
    const mesocycle = template.create();
    onCreate(mesocycle);
  };

  const handleManualCreate = () => {
    if (!name.trim()) {
      alert("Please enter a name for the mesocycle");
      return;
    }

    const mesocycle: Omit<Mesocycle, "id"> = {
      name,
      startDate: new Date().toISOString(),
      endDate: new Date(
        Date.now() + weeks * 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
      weeks: Array.from({ length: weeks }, (_, i) => ({
        weekNumber: i + 1,
        sessions,
      })),
      active: true,
    };

    onCreate(mesocycle);
  };

  const addSession = () => {
    const newSession: Session = {
      dayOfWeek: sessions.length + 1,
      name: `Session ${sessions.length + 1}`,
      exercises: [],
    };
    setSessions([...sessions, newSession]);
  };

  const addExercise = (sessionIndex: number) => {
    const newExercise: Exercise = {
      name: "New Exercise",
      sets: 3,
      targetRepsPerSet: 10,
    };
    const updatedSessions = [...sessions];
    updatedSessions[sessionIndex].exercises.push(newExercise);
    setSessions(updatedSessions);
  };

  const updateExercise = (
    sessionIndex: number,
    exerciseIndex: number,
    field: keyof Exercise,
    value: any
  ) => {
    const updatedSessions = [...sessions];
    (updatedSessions[sessionIndex].exercises[exerciseIndex] as any)[field] =
      value;
    setSessions(updatedSessions);
  };

  if (!mode) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm">
          <div className="px-4 py-3">
            <button
              onClick={onCancel}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Cancel
            </button>
          </div>
          <div className="px-4 pb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              Create New Mesocycle
            </h1>
          </div>
        </div>

        <div className="px-4 py-6 space-y-4">
          <button
            onClick={() => setMode("template")}
            className="w-full bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Use Template
            </h3>
            <p className="text-sm text-gray-600">
              Choose from predefined workout programs
            </p>
          </button>

          <button
            onClick={() => setMode("manual")}
            className="w-full bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Create Manually
            </h3>
            <p className="text-sm text-gray-600">
              Build your own custom mesocycle
            </p>
          </button>
        </div>
      </div>
    );
  }

  if (mode === "template") {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm">
          <div className="px-4 py-3">
            <button
              onClick={() => setMode(null)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
          </div>
          <div className="px-4 pb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              Choose Template
            </h1>
          </div>
        </div>

        <div className="px-4 py-6 space-y-4">
          {mesocycleTemplates.map((template, index) => (
            <button
              key={index}
              onClick={() => handleTemplateSelect(index)}
              className="w-full bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {template.name}
              </h3>
              <p className="text-sm text-gray-600">{template.description}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white shadow-sm">
        <div className="px-4 py-3">
          <button
            onClick={() => setMode(null)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
        <div className="px-4 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Create Manual Mesocycle
          </h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mesocycle Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., PPL Hypertrophy"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Weeks
          </label>
          <input
            type="number"
            min="1"
            max="12"
            value={weeks}
            onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Sessions</h3>
            <button
              onClick={addSession}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              + Add Session
            </button>
          </div>

          {sessions.map((session, sessionIndex) => (
            <div
              key={sessionIndex}
              className="bg-white rounded-lg shadow-sm p-4 mb-4"
            >
              <input
                type="text"
                value={session.name}
                onChange={(e) => {
                  const updated = [...sessions];
                  updated[sessionIndex].name = e.target.value;
                  setSessions(updated);
                }}
                className="text-lg font-semibold text-gray-900 mb-3 w-full border-b border-gray-200 pb-1 focus:outline-none focus:border-blue-500"
              />

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Exercises
                  </span>
                  <button
                    onClick={() => addExercise(sessionIndex)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Exercise
                  </button>
                </div>

                {session.exercises.map((exercise, exerciseIndex) => (
                  <div
                    key={exerciseIndex}
                    className="flex items-center space-x-2"
                  >
                    <input
                      type="text"
                      value={exercise.name}
                      onChange={(e) =>
                        updateExercise(
                          sessionIndex,
                          exerciseIndex,
                          "name",
                          e.target.value
                        )
                      }
                      className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded"
                      placeholder="Exercise name"
                    />
                    <input
                      type="number"
                      min="1"
                      value={exercise.sets}
                      onChange={(e) =>
                        updateExercise(
                          sessionIndex,
                          exerciseIndex,
                          "sets",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-16 px-2 py-1 text-sm border border-gray-200 rounded"
                      placeholder="Sets"
                    />
                    <input
                      type="number"
                      min="1"
                      value={exercise.targetRepsPerSet}
                      onChange={(e) =>
                        updateExercise(
                          sessionIndex,
                          exerciseIndex,
                          "targetRepsPerSet",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-16 px-2 py-1 text-sm border border-gray-200 rounded"
                      placeholder="Reps"
                    />
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={exercise.targetWeight || ""}
                      onChange={(e) =>
                        updateExercise(
                          sessionIndex,
                          exerciseIndex,
                          "targetWeight",
                          e.target.value
                            ? parseFloat(e.target.value)
                            : undefined
                        )
                      }
                      className="w-20 px-2 py-1 text-sm border border-gray-200 rounded"
                      placeholder="Weight"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <button
          onClick={handleManualCreate}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Create Mesocycle
        </button>
      </div>
    </div>
  );
};
